// Type definitions for prospect media (videos and documents)
// Ported from mobile implementation with web-specific adaptations

// An organization's video label. Flat vocabulary, unlike the category-and-options
// prospect tags in types/tags.ts — a video is "Bullpen" and "Pitching" at once.
// Matches API VideoTagResponse.
export interface VideoTag {
  id: string;
  name: string;
  /** True when the label came from the seeded starter vocabulary. */
  isDefault: boolean;
  displayOrder: number;
}

// Response from PUT .../videos/{videoId}/tags. unknownTagIds carries back any id that no
// longer resolves, so a stale picker can refetch rather than assume the tag was applied.
export interface SetVideoTagsResponse {
  videoId: string;
  tags: VideoTag[];
  unknownTagIds: string[];
}

// Every way a video list can be narrowed. Mirrors the API's VideoFilter: axes AND
// together, and tagIds is match-any within itself.
export interface VideoFilters {
  /** Free text, matched against title, prospect name, uploader name, and tag names. */
  searchTerm: string;
  /** Match-any set of video tag ids. */
  tagIds: string[];
  teamId: string | null;
  uploadedBy: string | null;
  /** Inclusive `YYYY-MM-DD` bounds on capture date. */
  uploadedAfter: string | null;
  uploadedBefore: string | null;
}

export const EMPTY_VIDEO_FILTERS: VideoFilters = {
  searchTerm: '',
  tagIds: [],
  teamId: null,
  uploadedBy: null,
  uploadedAfter: null,
  uploadedBefore: null,
};

/** True when the filters would narrow something — drives the "Clear" affordance. */
export const hasActiveVideoFilters = (filters: VideoFilters): boolean =>
  filters.searchTerm.trim() !== '' ||
  filters.tagIds.length > 0 ||
  filters.teamId !== null ||
  filters.uploadedBy !== null ||
  filters.uploadedAfter !== null ||
  filters.uploadedBefore !== null;

/** How many axes are narrowing, for the filter button's badge. */
export const countActiveVideoFilters = (filters: VideoFilters): number =>
  (filters.searchTerm.trim() !== '' ? 1 : 0) +
  (filters.tagIds.length > 0 ? 1 : 0) +
  (filters.teamId !== null ? 1 : 0) +
  (filters.uploadedBy !== null ? 1 : 0) +
  (filters.uploadedAfter !== null || filters.uploadedBefore !== null ? 1 : 0);

/** One person who has captured video in the org. Populates the "uploaded by" picker. */
export interface VideoUploader {
  id: string;
  name: string;
  videoCount: number;
}

/** A team with at least one rostered prospect that has footage. */
export interface VideoFilterTeam {
  id: string;
  name: string;
}

/** Everything the filter UI's pickers need, from GET /api/videos/filter-options. */
export interface VideoFilterOptions {
  tags: VideoTag[];
  uploaders: VideoUploader[];
  teams: VideoFilterTeam[];
}

// Core video type matching API response
export interface ProspectVideo {
  id: string;
  prospectId: string;
  gcsUrl: string;
  thumbnailGcsUrl: string;
  contentType: string;
  fileExtension: string;
  durationSeconds?: number | null;
  fileSizeBytes?: number | null;
  uploadedBy: string;
  uploadedByName?: string | null;
  createdAt: string;
  urlExpiresAt: string;
  thumbnailUrlExpiresAt?: string | null;

  // Clip metadata. When sourceVideoId is set this row is a clip: gcsUrl points at the
  // SOURCE video's file, and only [clipStartSeconds, clipEndSeconds] should be played.
  sourceVideoId?: string | null;
  clipStartSeconds?: number | null;
  clipEndSeconds?: number | null;
  title?: string | null;

  /** Video-level labels in the organization's display order. Empty when untagged. */
  tags?: VideoTag[];
}

/**
 * A short-lived link that saves a video instead of playing it
 * (GET .../videos/{id}/download-url). The link is signed with an attachment
 * Content-Disposition, so the browser downloads `fileName`.
 */
export interface VideoDownloadUrlResponse {
  downloadUrl: string;
  expiresAt: string;
  fileName: string;
  contentType: string;
  /** Size of the file the link serves. For a clip, the source's size. */
  fileSizeBytes: number;
  /** True for a clip: clips have no file of their own, so the link is the full source video. */
  isFullSourceVideo: boolean;
}

/** True when the video is a clip of another video rather than an original upload. */
export const isClip = (video: {
  sourceVideoId?: string | null;
  clipStartSeconds?: number | null;
  clipEndSeconds?: number | null;
}): boolean =>
  Boolean(
    video.sourceVideoId &&
      video.clipStartSeconds != null &&
      video.clipEndSeconds != null
  );

// One clip window submitted to the API. Times are seconds from the start of the source.
export interface CreateVideoClipRequest {
  title?: string;
  startSeconds: number;
  endSeconds: number;
  /** Stable client-side identity so a retried save doesn't duplicate the clip. */
  clientGeneratedId?: string;
}

export interface CreateVideoClipsRequest {
  clips: CreateVideoClipRequest[];
}

export interface VideoClipCreatedResponse {
  clipId: string;
  title?: string | null;
  startSeconds: number;
  endSeconds: number;
  /** Signed PUT URL for the clip's poster frame. Empty when the API could not issue one. */
  thumbnailUploadUrl: string;
}

export interface CreateVideoClipsResponse {
  sourceVideoId: string;
  clips: VideoClipCreatedResponse[];
}

/** A clip the user has marked out in the editor but has not saved yet. */
export interface PendingClip {
  /** Local-only id used as the React key and as the idempotency key on save. */
  id: string;
  title: string;
  startSeconds: number;
  endSeconds: number;
}

// Request to generate presigned upload URLs
export interface VideoUploadUrlRequest {
  fileExtension: string;
  contentType: string;
  fileSizeBytes?: number;
  durationSeconds?: number;
}

// Response containing presigned URLs for video and thumbnail upload
export interface VideoUploadUrlResponse {
  mediaId: string;
  uploadUrl: string;
  gcsUrl: string;
  thumbnailUploadUrl: string;
  thumbnailGcsUrl: string;
}

// Upload progress tracking (6 stages for web - no compression)
export interface VideoUploadProgress {
  stage: 'generating_thumbnail' | 'uploading_video' | 'uploading_thumbnail' | 'complete';
  percent: number; // 0-100
  message: string;
}

// Result of upload operation
export interface VideoUploadResult {
  success: boolean;
  mediaId?: string;
  gcsUrl?: string;
  thumbnailGcsUrl?: string;
  error?: string;
  uploadTimeMs: number;
}

// Cached signed URL with expiration
export interface CachedSignedUrl {
  signedUrl: string;
  expiresAt: Date;
}

// Map-based cache for signed URLs
export type SignedUrlCache = Map<string, CachedSignedUrl>;

// Video with prospect information (for organization-wide video list)
// Matches API OrganizationVideoResponse
export interface VideoWithProspect {
  id: string;
  prospectId: string;
  prospectFirstName: string;
  prospectLastName: string;
  gcsUrl: string;
  thumbnailGcsUrl: string | null;
  contentType: string;
  durationSeconds: number | null;
  fileExtension: string;
  uploadedBy: string;
  uploadedByName?: string | null;
  createdAt: string;
  urlExpiresAt: string;
  thumbnailUrlExpiresAt: string | null;

  // Clip metadata — see ProspectVideo.
  sourceVideoId?: string | null;
  clipStartSeconds?: number | null;
  clipEndSeconds?: number | null;
  title?: string | null;

  /** Video-level labels — see ProspectVideo. */
  tags?: VideoTag[];
}

// Document types - ported from mobile implementation

// Core document type matching API response
export interface ProspectDocument {
  id: string;
  prospectId: string;
  gcsUrl: string;                         // Signed GCS URL (1-hour expiry)
  contentType: string;                    // e.g., "application/pdf"
  fileExtension: string;                  // e.g., ".pdf"
  fileSizeBytes: number;
  title?: string | null;                  // User-provided display name
  isHandwrittenNote: boolean;             // Flag for handwritten notes
  uploadedBy: string;                     // User ID
  createdAt: string;                      // ISO 8601
  urlExpiresAt: string;                   // Signed URL expiry time
}

// Request to generate presigned upload URL for document
export interface DocumentUploadUrlRequest {
  fileExtension: string;                  // e.g., ".pdf"
  contentType: string;                    // e.g., "application/pdf"
  fileSizeBytes?: number;
  title?: string;                         // Optional user-provided name
  isHandwrittenNote?: boolean;            // Flag for handwritten notes
}

// Response containing presigned URL for document upload
export interface DocumentUploadUrlResponse {
  mediaId: string;                        // Unique document ID
  uploadUrl: string;                      // Presigned GCS URL (PUT)
  gcsUrl: string;                        // Permanent GCS URL
}

// Upload progress tracking (2 stages for documents)
export interface DocumentUploadProgress {
  stage: 'requesting_url' | 'uploading' | 'complete';
  percent: number;                        // 0-100
  message: string;                        // User-facing status
}

// Result of upload operation
export interface DocumentUploadResult {
  success: boolean;
  mediaId?: string;
  gcsUrl?: string;
  error?: string;
  uploadTimeMs: number;
}
